/**
 * Faixas de crescimento progressivo da media historica (Origem da meta do
 * colaborador = Media historica, Tipo de crescimento = Progressivo).
 *
 * Nao e o mesmo conceito das faixas de calculoMixin.js (bonus por faixa de
 * QUANTIDADE REALIZADA, por colaborador+item). Aqui a faixa e sobre a PROPRIA
 * MEDIA do colaborador, compartilhada por toda a configuracao da media — por
 * isso um util proprio, em vez de estender o mixin existente.
 *
 * Regra corrigida com o negocio em 2026-09-10, depois de ver a tabela real da
 * campanha: o "ate" digitado e so ROTULO (numero redondo, pra comunicacao —
 * "8 a 9", "10 a 15", "16 a 20"), NAO o limite matematico. Quem decide onde
 * uma faixa termina de verdade e o "de" da PROXIMA faixa, nao o "ate" dela
 * mesma. Media 9,5 com faixas "8 a 9" e "10 a 15" cai na de 8 (vale ate
 * pouco antes do 10, o "de" da faixa seguinte) — e nao "cai fora" so porque
 * o rotulo dizia "ate 9".
 *
 * Por isso cada faixa e independente (sem herdar "de" da anterior) e a regra
 * de calculo e "a faixa de maior 'de' que ainda e <= a media" — ver
 * resolverPercentualPorFaixa em metaMediaUtils.js. O "ate" so entra pra
 * gerar o rotulo visual (ver calcularFaixasEfetivas), nunca pra decidir
 * qual faixa vale.
 */

/**
 * Primeira faixa de uma configuracao nova: "de" trava no piso (decisao de
 * negocio: a faixa mais baixa sempre comeca exatamente onde o piso termina),
 * o resto fica em branco para o usuario preencher.
 * @param {number|string} piso
 */
export function criarFaixaInicial(piso) {
  return { de: piso, ate: "", crescimento: "" };
}

/**
 * Acrescenta uma faixa em branco. Sem herdar nada da anterior — "de" e
 * "ate" sao digitados livremente, cada faixa e independente.
 * @param {Array<{de:number|string, ate:number|string, crescimento:number|string}>} faixas
 */
export function adicionarFaixa(faixas) {
  return [...(faixas || []), { de: "", ate: "", crescimento: "" }];
}

/**
 * So bloqueia adicionar mais uma faixa enquanto a ultima ja criada ainda nao
 * tem "de" preenchido — evita acumular varias linhas em branco.
 */
export function podeAdicionarFaixa(faixas) {
  const lista = faixas || [];
  if (lista.length === 0) return true;
  const ultima = lista[lista.length - 1];
  return ultima.de !== "" && ultima.de != null && !isNaN(parseFloat(ultima.de));
}

/**
 * Qualquer faixa pode ser removida agora (nao ha mais cadeia de dependencia
 * entre elas) — so preserva pelo menos uma.
 */
export function podeRemoverFaixa(faixas) {
  return (faixas || []).length > 1;
}

export function removerFaixa(faixas, idx) {
  return (faixas || []).filter((_, i) => i !== idx);
}

export function faixaDeErro(faixa) {
  if (!faixa || faixa.de === "" || faixa.de == null) return "Informe o \"de\" desta faixa";
  if (isNaN(parseFloat(faixa.de))) return "\"De\" inválido";
  return "";
}

/**
 * "Ate" e so rotulo (ver comentario no topo do arquivo) — continua opcional
 * em QUALQUER faixa, nao so na de maior "de". Quando preenchido, so precisa
 * fazer sentido (maior que o proprio "de"); nao precisa bater com o "de" de
 * nenhuma outra faixa.
 */
export function faixaAteErro(faixa) {
  if (!faixa || faixa.ate === "" || faixa.ate == null) return "";
  const de = parseFloat(faixa.de);
  const ate = parseFloat(faixa.ate);
  if (isNaN(ate) || ate <= de) return `"Até" deve ser maior que ${faixa.de}`;
  return "";
}

export function faixaCrescimentoErro(faixa) {
  if (!faixa || faixa.crescimento === "" || faixa.crescimento == null) return "Informe o crescimento desta faixa";
  if (isNaN(parseFloat(faixa.crescimento))) return "Crescimento inválido";
  return "";
}

/**
 * Ordena por "de" numerico crescente — base tanto da validacao quanto da
 * exibicao (calcularFaixasEfetivas).
 */
function ordenarPorDe(faixas) {
  return [...(faixas || [])].sort((a, b) => parseFloat(a.de) - parseFloat(b.de));
}

/**
 * Monta, para cada faixa (ordenada por "de"), o intervalo que ela REALMENTE
 * aplica: comeca no proprio "de" e vai ate o "de" da proxima faixa (exclusive)
 * — nao ate o "ate" que foi digitado. A ultima (maior "de") fica sem teto.
 *
 * E a "indicacao visual" pedida pelo negocio em 2026-09-10: mostra pra quem
 * esta cadastrando o que o sistema vai aplicar de verdade, nao so o rotulo
 * que foi digitado — os dois podem ser numeros diferentes de proposito (ver
 * comentario no topo do arquivo).
 * @param {Array<{de, ate, crescimento}>} faixas
 * @returns {Array<{de, ate, crescimento, deEfetivo:number, ateEfetivo:?number}>}
 */
export function calcularFaixasEfetivas(faixas) {
  const ordenadas = ordenarPorDe(faixas);
  return ordenadas.map((faixa, idx) => {
    const proxima = ordenadas[idx + 1];
    return {
      ...faixa,
      deEfetivo: parseFloat(faixa.de),
      ateEfetivo: proxima ? parseFloat(proxima.de) : null
    };
  });
}

/**
 * Consolida os bloqueios de todo o conjunto de faixas — espelha
 * verificaFaixasCrescimento (CampaignGoalValidator, API), para o mesmo erro
 * aparecer no cadastro antes de tentar salvar.
 * @param {Array<{de, ate, crescimento}>} faixas
 * @param {number|string} piso
 * @returns {string[]} pendencias, vazio quando o conjunto está completo
 */
export function pendenciasFaixasCrescimento(faixas, piso) {
  const lista = faixas || [];
  const pendencias = [];

  if (lista.length === 0) {
    pendencias.push("ao menos uma faixa de crescimento");
    return pendencias;
  }

  lista.forEach(faixa => {
    if (faixaDeErro(faixa)) pendencias.push('"de" de alguma faixa');
    if (faixaCrescimentoErro(faixa)) pendencias.push("crescimento de alguma faixa");
    if (faixaAteErro(faixa)) pendencias.push('"até" de alguma faixa inválido');
  });

  // So faz sentido comparar "de"s depois que todos estiverem preenchidos e
  // numericos — senao NaN polui a comparacao com falsos positivos.
  const comDeValido = lista.filter(f => !faixaDeErro(f));
  if (comDeValido.length === lista.length) {
    const ordenadas = ordenarPorDe(lista);

    const pisoNum = parseFloat(piso);
    if (!isNaN(pisoNum) && parseFloat(ordenadas[0].de) !== pisoNum) {
      pendencias.push('a faixa de menor "de" precisa começar no piso');
    }

    for (let i = 1; i < ordenadas.length; i++) {
      if (parseFloat(ordenadas[i].de) === parseFloat(ordenadas[i - 1].de)) {
        pendencias.push('duas faixas não podem ter o mesmo "de"');
        break;
      }
    }
  }

  return [...new Set(pendencias)];
}

export function faixasCrescimentoValidas(faixas, piso) {
  return pendenciasFaixasCrescimento(faixas, piso).length === 0;
}
